import { useCallback, useState, type ReactNode } from 'react'
import type { JobOutput } from '@/api/types'
import { useAssetLibrary } from '@/context/AssetLibraryContext'
import { useToast } from '@/context/ToastContext'
import { saveAs } from '@/lib/download'
import { toRelUrl } from '@/lib/mediaUrl'
import { Icon } from './icons'
import { ImageLightbox } from './ImageLightbox'
import { SmartImage } from './SmartImage'
import { Badge, Button, ClearButton, CopyButton, Empty } from './ui'

const ratioClass = (r?: string) => {
  if (!r) return ''
  return r === '16:9' ? ' result-tile--16-9' : r === '3:4' ? ' result-tile--3-4' : r === '9:16' ? ' result-tile--9-16' : ''
}

/** 图片 / 视频结果网格：点击放大 / 下载 / 存入素材库
 *
 * `bare` 模式只渲染网格本身（无外层卡片与标题头），供任务卡 / 作品图集内嵌。
 * ⚠️ **为什么要有这个模式而不是另写一个网格**：结果网格曾经在
 * `ResultGrid` 与生成页里各有一份，漏改一次就是「视频结果被滤掉」那种事故。
 * 内嵌需求应该加开关，不应该加第二份实现。
 */
export function ResultGrid({
  outputs,
  aspectRatio,
  title = '本轮结果',
  emptyText = '左侧填好参数后点击生成，结果会出现在这里。',
  emptyTitle = '还没有结果',
  onClear,
  bare,
  itemActions,
}: {
  outputs: JobOutput[]
  aspectRatio?: string
  title?: string
  emptyText?: string
  /**
   * 空态**标题**。默认「还没有结果」，但「本轮结果刚被收起」不是同一件事 ——
   * 标题照写「还没有结果」，用户读到的还是「产物没了」，改描述也没用
   * （粗体那行才是被读进去的那句）。
   */
  emptyTitle?: string
  /** 收起这一栏的产出预览（`clearResults`，不动历史记录）；不传则头里不出现清空按钮 */
  onClear?: () => void
  /** 只出网格（内嵌用）：无卡片、无标题头、无结果时渲染 null */
  bare?: boolean
  /**
   * 单张产出上的页面专属操作（如「我的作品」里的删除这一张）。
   * 渲染在操作条与放大预览里 —— 用插槽而不是让调用方自己画网格：
   * 网格一旦有了第二份实现，就会出现「视频结果被滤掉」那类只在一边修的 bug。
   */
  itemActions?: (o: JobOutput, i: number) => ReactNode
}) {
  const { addGenerated } = useAssetLibrary()
  const toast = useToast()
  // 只排掉文本（文本有自己的 TextResult 卡片）。
  // 以前这里写的是 `type === 'image'`，于是 VideoStudio 传进来的视频结果被全部滤掉，
  // 「视频结果」卡片永远停在「共 0 张 / 还没有结果」—— 视频生成了却看不到。
  const media = outputs.filter((o) => o.type === 'image' || o.type === 'video')

  // 放大预览的下标；null = 未打开
  const [zoomAt, setZoomAt] = useState<number | null>(null)

  const fileName = useCallback(
    (i: number, kind?: string) => `${title}-${i + 1}.${kind === 'video' ? 'mp4' : 'png'}`,
    [title],
  )

  const saveToLibrary = useCallback(
    (o: JobOutput, i: number) => {
      addGenerated({
        id: `asset-${Date.now().toString(36)}-${i}`,
        url: o.url,
        thumbUrl: o.thumbUrl ?? o.url,
        name: `${title} #${i + 1}`,
        kind: o.type,
        createdAt: new Date().toISOString(),
        source: 'generated',
      })
      toast.success('已存入素材库')
    },
    [addGenerated, title, toast],
  )

  if (!media.length) {
    if (bare) return null
    return (
      <div className="card">
        <div className="card__head">
          <Icon name="grid" size={16} style={{ color: 'hsl(var(--primary-strong))' }} />
          <div className="grow">
            <div className="card__title">{title}</div>
            <div className="card__hint">共 0 个</div>
          </div>
        </div>
        <Empty icon="grid" title={emptyTitle} text={emptyText} />
      </div>
    )
  }

  const grid = (
    <div className="result-grid">
      {media.map((o, i) => (
        <div key={`${o.url}-${i}`} className={`result-tile${ratioClass(aspectRatio)}`}>
          <SmartImage src={o.thumbUrl ?? o.url} alt={`结果 ${i + 1}`} kind={o.type} />

          {/* 放大热区：铺满整块缩略图，并压在操作条下面（z-index），
              这样「点图放大」和「点按钮下载」不会互相抢事件 */}
          <button
            type="button"
            className="result-tile__zoom"
            onClick={() => setZoomAt(i)}
            aria-label={`放大查看第 ${i + 1} 张`}
            title="点击放大"
          />

          <span className="result-tile__idx">{i + 1}</span>
          <span className="result-tile__zoom-hint">
            <Icon name="expand" size={13} />
          </span>
          <div className="result-tile__bar">
            <Button size="sm" icon="download" onClick={() => void saveAs(o.url, fileName(i, o.type))}>
              下载
            </Button>
            <Button size="sm" icon="folder" onClick={() => saveToLibrary(o, i)}>
              存素材
            </Button>
            {itemActions?.(o, i)}
          </div>
        </div>
      ))}
    </div>
  )

  const lightbox =
    zoomAt !== null ? (
      <ImageLightbox
        items={media}
        index={zoomAt}
        onIndex={setZoomAt}
        onClose={() => setZoomAt(null)}
        title={title}
        renderActions={(o, i) => (
          <>
            <Button size="sm" icon="download" onClick={() => void saveAs(o.url, fileName(i, o.type))}>
              下载
            </Button>
            <Button size="sm" icon="folder" onClick={() => saveToLibrary(o, i)}>
              存素材
            </Button>
            {itemActions?.(o, i)}
          </>
        )}
      />
    ) : null

  if (bare) {
    return (
      <>
        {grid}
        {lightbox}
      </>
    )
  }

  return (
    <div className="card">
      <div className="card__head">
        <Icon name="grid" size={16} style={{ color: 'hsl(var(--primary-strong))' }} />
        <div className="grow">
          <div className="card__title">{title}</div>
          <div className="card__hint">共 {media.length} 个 · 点击可放大查看</div>
        </div>
        <Badge tone="success" icon="check">
          已完成
        </Badge>
        {onClear ? (
          <ClearButton
            count={media.length}
            /* 说明里第一句必须是**它不动什么**：这颗按钮长在结果卡上，
               用户点它想清的就是这一栏 —— 上一版这里写「清除本页结果记录」，
               而实现把历史记录也一起清了（2026-09-30 报障）。 */
            title="清空这一栏的产出预览（历史记录不动，点开任意一条仍能看到）"
            onClear={onClear}
          />
        ) : null}
      </div>
      <div className="card__body card__body--tight">{grid}</div>
      {lightbox}
    </div>
  )
}

/**
 * 音频结果卡片（语音合成）。
 *
 * ## 为什么必须是**第三张卡**，而不是塞进 `ResultGrid`
 *
 * `ResultGrid` 是**图片/视频网格**：每格靠 `SmartImage` 出画面、点格子放大。
 * 音频既没有画面也没有「放大」这回事，塞进去只能得到一格碎图标
 * （`<img src="x.mp3">` 必然失败 —— 与当年 `<img src="x.mp4">` 是同一个坑）。
 *
 * ⚠️ 而**更早的问题**是它压根到不了这里：`ResultGrid` 的过滤条件是
 * `o.type === 'image' || o.type === 'video'`，音频产出**会被静默丢掉** ——
 * 任务状态 `succeeded`、扣费正常、右栏却写着「共 0 个」。
 * 这类「产物生成了但看不见」正是 `ResultGrid` 头部注释记着的那次事故
 * （视频结果被滤掉）的同构复发，所以三张卡的分工要写下来：
 *
 *   · `ResultGrid`   —— 有画面、可放大（image / video）
 *   · `AudioResult`  —— 有声音、可试听（audio）  ← 本组件
 *   · `TextResult`   —— 可编辑、可复制（text）
 *
 * ## 两条口径
 *
 * ① **不自动播放**。一次合成可能出多条，进页面就一起响是灾难。
 *    用原生 `<audio controls>`：播放/暂停/进度/音量都由浏览器实现，
 *    自己不写一套播放器 —— 自研播放器要处理 seek / 缓冲 / 音量记忆，
 *    每一处都能写出「看着在放、其实没声」的假象。
 * ② **不重复花钱**：这里播的是**已经付过费的成品**（相对地址直连本站静态目录），
 *    点多少次都不会再合成一次 —— 与 `VoicePreviewButton`（试听那一次会真花钱）
 *    是两件事，别把两者的计费口径混起来。
 */
export function AudioResult({
  outputs,
  title = '语音结果',
  emptyText = '左侧填好口播稿并选好音色后点击生成，音频会出现在这里。',
  emptyTitle = '还没有结果',
  onClear,
  onPlay,
  bare,
}: {
  outputs: JobOutput[]
  title?: string
  /** 空态说明。区分「还没有生成过」与「本轮结果刚被收起」，同 `ResultGrid` */
  emptyText?: string
  /** 空态标题，同 `ResultGrid.emptyTitle`：收起 ≠ 没生成过 */
  emptyTitle?: string
  /** 收起这一栏的产出预览（`clearResults`，不动历史记录）；不传则头里不出现清空按钮 */
  onClear?: () => void
  /**
   * 只出音频卡本体（嵌进右栏那条**合并流**里用）：无卡片外壳、无标题头，
   * 无产出时渲染 `null`。与 `ResultGrid.bare` **同义**。
   *
   * 为什么需要它：右栏「产出 + 历史」合并成一张卡之后，产出段再自带一个卡头，
   * 就成了「一张卡里套一张同款卡」—— 标题写两遍、边框套两层。裸渲染之后，
   * 「共 N 条」与「清空」都由外面那张卡的头承担，这里只负责把音频摆出来。
   * ⚠️ 空态在 `bare` 下**必须返回 null 而不是空态块**：产出为空时要让它
   * 彻底不占地方（下方历史列表自己的空态会说话），否则又回到
   * 「上面一张空卡、下面一张有记录的卡」—— 那正是这次要修的分裂感。
   */
  bare?: boolean
  /**
   * 「把这条送到底部试听条并开始播」—— 传了才出现那颗播放键。
   *
   * 与下面那张卡里的原生 `<audio controls>` **不是两个抢着播的实现**：
   * 原生控件是**就地核对**（点开这一条，立刻听是不是你要的），
   * 而这颗键是**送到常驻条**（它跨产出、跨滚动一直挂在那儿，边改稿边听）。
   * 两者播的是同一条 URL 的两个播放实例，不会同时出声 —— 详情见
   * `components/AudioPlayerBar.tsx` 的说明。
   */
  onPlay?: (o: JobOutput) => void
}) {
  const { addGenerated } = useAssetLibrary()
  const toast = useToast()
  const audios = outputs.filter((o) => o.type === 'audio' && o.url)

  const saveToLibrary = (o: JobOutput, i: number) => {
    addGenerated({
      id: `asset-${Date.now().toString(36)}-${i}`,
      url: o.url,
      thumbUrl: o.url,
      name: `${title} #${i + 1}`,
      kind: o.type,
      createdAt: new Date().toISOString(),
      source: 'generated',
    })
    toast.success('已存入素材库')
  }

  if (!audios.length) {
    /* 裸渲染下产出为空就整段不出现 —— 空态由外面那层（历史列表）说话，见 `bare` 注释 */
    if (bare) return null
    return (
      <div className="card">
        <div className="card__head">
          <Icon name="wave" size={16} style={{ color: 'hsl(var(--primary-strong))' }} />
          <div className="grow">
            <div className="card__title">{title}</div>
            <div className="card__hint">共 0 条</div>
          </div>
        </div>
        <Empty icon="wave" title={emptyTitle} text={emptyText} />
      </div>
    )
  }

  /*
   * 音频本体。`bare` 下它就是返回值 —— 卡片外壳与标题头都交给外面那一层。
   *
   * ⚠️ `bare` 时**必须摘掉 `card__body`**：它自带 20px 内边距，而外面的
   * `CardBody tight` 还有 16px，叠起来产出段会比历史列表凭空多缩进 20px
   * （两块并排时一眼就看得出没对齐）。
   */
  const body = (
    <div className={bare ? 'stack' : 'card__body stack'}>
        {audios.map((o, i) => (
          <div key={`${o.url}-${i}`} className="card" style={{ boxShadow: 'none' }}>
            <div className="card__head">
              {/* 标题必须能截断：右栏只有 ~250px 可用宽，而这条头里还要放三个操作键 */}
              <div className="card__title nowrap" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                第 {i + 1} 条
                {o.model ? <span className="muted"> · {o.model}</span> : null}
              </div>
              <div className="grow" />
              {/*
                三个操作键**一律图标化**（2026-10-08 实测）。
                原来是「在底部播放 / 下载 / 存素材」三个文字键 —— 在 /tts 那 250px 的
                右栏里，它们把「第 1 条 · minimax-tts」挤成了**四行竖排**，最后一个键
                还被顶出可视区（探针 `_probe-merge-live.py` 的截图里能看到）。
                语义改由图标 + `title` 承担；`aria-label` 一起给上，不然读屏只剩图形。
              */}
              {onPlay ? (
                <Button
                  size="sm"
                  variant="outline"
                  icon="play"
                  className="btn--icon"
                  title="送到底部试听条播放"
                  aria-label="送到底部试听条播放"
                  onClick={() => onPlay(o)}
                />
              ) : null}
              <Button
                size="sm"
                icon="download"
                className="btn--icon"
                title="下载这条音频"
                aria-label="下载这条音频"
                onClick={() => void saveAs(o.url, `${title}-${i + 1}.mp3`, 'mp3')}
              />
              <Button
                size="sm"
                icon="folder"
                className="btn--icon"
                title="存入素材库"
                aria-label="存入素材库"
                onClick={() => saveToLibrary(o, i)}
              />
            </div>
            <div className="card__body">
              {/* 用原生播放器：播放/进度/音量交给浏览器，自己不写第二套
                  （自研播放器最容易出「看着在放、其实没声」）。 */}
              <audio
                controls
                preload="metadata"
                src={toRelUrl(o.url)}
                style={{ width: '100%' }}
                data-audio-result={i}
              />
            </div>
          </div>
        ))}
    </div>
  )

  /* 裸渲染：只有音频本体，没有卡片外壳与标题头（标题、张数、清空由外面那张卡承担） */
  if (bare) return body

  return (
    <div className="card">
      <div className="card__head">
        <Icon name="wave" size={16} style={{ color: 'hsl(var(--primary-strong))' }} />
        <div className="grow">
          <div className="card__title">{title}</div>
          <div className="card__hint">共 {audios.length} 条 · 可直接试听与下载</div>
        </div>
        <Badge tone="success" icon="check">
          已完成
        </Badge>
        {onClear ? (
          <ClearButton
            count={audios.length}
            unit="条"
            title="清空这一栏的产出预览（历史记录不动，点开任意一条仍能看到）"
            onClear={onClear}
          />
        ) : null}
      </div>
      {body}
    </div>
  )
}

/** 文本结果卡片（文案类） */
export function TextResult({
  outputs,
  title = '生成结果',
  emptyText = '填好产品信息后点击生成，文案会出现在这里。',
  emptyTitle = '还没有结果',
  onClear,
  bare,
}: {
  outputs: JobOutput[]
  title?: string
  /**
   * 空态说明。调用方传它是为了区分「还没有生成过」和「本轮结果刚被收起」——
   * 后者说成前者，用户会读成「文案被删了」（同 `ResultGrid.emptyText`）。
   */
  emptyText?: string
  /** 空态标题，同 `ResultGrid.emptyTitle`：收起 ≠ 没生成过 */
  emptyTitle?: string
  /** 收起这一栏的产出预览（`clearResults`，不动历史记录）；不传则头里不出现清空按钮 */
  onClear?: () => void
  /**
   * 只出文本卡本体（嵌进右栏那条**合并流**里用）：无卡片外壳、无标题头，
   * 无产出时渲染 `null`。与 `ResultGrid.bare` / `AudioResult.bare` 同义，
   * 理由见 `AudioResult` 上那段注释（一张卡里不该再套一张同款卡）。
   */
  bare?: boolean
}) {
  const texts = outputs.filter((o) => o.type === 'text' && o.text)
  if (!texts.length) {
    /* 裸渲染下产出为空就整段不出现 —— 空态由外面那层说话，同 `AudioResult.bare` */
    if (bare) return null
    return (
      <div className="card">
        <div className="card__head">
          <Icon name="text" size={16} style={{ color: 'hsl(var(--primary-strong))' }} />
          <div className="grow">
            <div className="card__title">{title}</div>
            <div className="card__hint">共 0 份</div>
          </div>
        </div>
        <Empty icon="text" title={emptyTitle} text={emptyText} />
      </div>
    )
  }
  /*
   * 文案本体。`bare` 下它就是返回值 —— 卡片外壳与标题头都交给外面那一层。
   * 同 `AudioResult`：`bare` 时必须摘掉 `card__body`，否则与外面的
   * `CardBody tight` 叠成两层内边距。
   */
  const body = (
    <div className={bare ? 'stack' : 'card__body stack'}>
        {texts.map((o, i) => (
          <div key={i} className="card" style={{ boxShadow: 'none' }}>
            <div className="card__head">
              <div className="card__title">版本 {i + 1}</div>
              <div className="grow" />
              <CopyButton text={o.text ?? ''} />
            </div>
            <div className="card__body">
              <textarea
                className="textarea"
                style={{ minHeight: 260, fontFamily: 'var(--font-body)' }}
                defaultValue={o.text}
              />
            </div>
          </div>
        ))}
    </div>
  )

  /* 裸渲染：只有文案本体，没有卡片外壳与标题头 */
  if (bare) return body

  return (
    <div className="card">
      <div className="card__head">
        <Icon name="text" size={16} style={{ color: 'hsl(var(--primary-strong))' }} />
        <div className="grow">
          <div className="card__title">{title}</div>
          <div className="card__hint">共 {texts.length} 份 · 可直接编辑后复制</div>
        </div>
        {onClear ? (
          <ClearButton
            count={texts.length}
            unit="份"
            title="清空这一栏的产出预览（历史记录不动，点开任意一条仍能看到）"
            onClear={onClear}
          />
        ) : null}
      </div>
      {body}
    </div>
  )
}
